// src/components/ErrorBoundary/ErrorBoundary.tsx
import React, { useEffect } from "react";
import type { FallbackProps } from "react-error-boundary";
import { ErrorBoundary as ReactErrorBoundary } from "react-error-boundary";
import { Button } from "@mantine/core";
import { IconLogout, IconRefresh } from "@tabler/icons-react";
import sadrobot from "../../assets/sadrobot.png";
import "./ErrorBoundary.scss";
import { AppRoutes, error_reports } from "../../constants";
import { sendEmailData } from "@api/email";
import type { SendEmailPayload } from "@components/Tax Pro Only/Modals/EmailModal/types.ts";
import { emitDebugEvent } from "@utils/debug/eventIntegration";

interface UndefinedField {
  storage: string;
  key: string;
  value: string | null;
}

// Utility functions
const getPersonalInfo = (): { email?: string } => {
  const personalInfo = localStorage.getItem("personalInfo");
  return personalInfo ? JSON.parse(personalInfo) : {};
};

const email: string = getPersonalInfo()?.email ?? "Not Available";

const findUndefinedFields = (): UndefinedField[] => {
  const undefinedFields: UndefinedField[] = [];

  // Helper function to check storage
  const checkStorage = (storage: Storage, storageName: string): void => {
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      const value = storage.getItem(key!);
      if (value === "undefined") {
        undefinedFields.push({
          storage: storageName,
          key: key!,
          value,
        });
      }
    }
  };

  checkStorage(localStorage, "localStorage");
  checkStorage(sessionStorage, "sessionStorage");

  return undefinedFields;
};

const sendErrorToFreshdesk = async (error: Error): Promise<void> => {
  const undefinedFields = findUndefinedFields();
  const undefinedFieldsHtml = undefinedFields
    .map(
      (field) =>
        `<p><strong>${field.storage}:</strong> ${field.key} = ${field.value}</p>`
    )
    .join("");

  const payload: SendEmailPayload = {
    to: ["help@smartwiz.io"],
    subject: "New Error Logged in Application",
    body: `
      <h2>Error Report</h2>
      <p><strong>Error Message:</strong> ${error.message}</p>
      <p><strong>Stack Trace:</strong></p>
      <pre>${error.stack}</pre>
      <p><strong>User Agent:</strong> ${navigator.userAgent}</p>
      <p><strong>URL:</strong> ${window.location.href}</p>
      <p><strong>Timestamp:</strong> ${new Date().toISOString()}</p>
      <p><strong>Email:</strong> ${email}</p>
      ${undefinedFieldsHtml}
    `,
    attachments: [],
  };

  try {
    if (error_reports) {
      await sendEmailData(payload);
      console.warn("Error sent to Freshdesk");
    } else {
      console.warn("Error reports not enabled", payload);
    }
  } catch (err) {
    console.error("Failed to send error to Freshdesk:", err);
  }
};

// ErrorFallback Component
const ErrorFallback: React.FC<FallbackProps> = ({
  error,
  resetErrorBoundary,
}) => {
  useEffect(() => {
    // Emit debug event for React error boundary
    emitDebugEvent("error", "REACT_ERROR_BOUNDARY", "web", {
      errorMessage: error.message?.substring(0, 200) || "Unknown error",
      errorName: error.name || "Error",
      url: window.location.href,
      componentStack: error.stack?.substring(0, 200) || undefined,
    });

    sendErrorToFreshdesk(error);
  }, [error]);

  const handleLogout = (): void => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = AppRoutes.login;
  };

  return (
    <div className="error-fallback" role="alert">
      <img src={sadrobot} alt="robot" className="error-fallback-img" />
      <h1>Oops! Something went wrong.</h1>
      <p>
        We're sorry for the inconvenience. Please try logging out and logging in
        again.
      </p>
      <p>
        If the issue persists, close all tabs and log out, then log in again. If
        the problem continues, contact support at{" "}
        <a href="mailto:help@smartwiz.io">help@smartwiz.io</a>
      </p>
      <pre className="error-message">{error.message}</pre>
      <div className="error-fallback-btns">
        <Button
          onClick={resetErrorBoundary}
          className="error-fallback-btn"
          size="lg"
          leftSection={<IconRefresh />}
        >
          Try again
        </Button>
        <Button
          onClick={handleLogout}
          color="red"
          leftSection={<IconLogout />}
          size="lg"
        >
          Logout
        </Button>
      </div>
    </div>
  );
};

// CustomErrorBoundary Component
const CustomErrorBoundary: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return (
    <ReactErrorBoundary FallbackComponent={ErrorFallback}>
      {children}
    </ReactErrorBoundary>
  );
};

export default CustomErrorBoundary;
